import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { GetAllWarehousesParams } from "../types";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";

export function useWarehouseFilters() {
  const { getFilterValue, isInitialized } = useDatatableContext();
  const { page, pageSize } = useTablePagination();
  const [searchParams] = useSearchParams();
  const sortAttribute = searchParams.get("sortAttribute") || "";
  const sortOrder = searchParams.get("sortOrder") || "";

  // Get date filters from context
  const period = useMemo(() => getFilterValue<number>("period") || 30, [getFilterValue]);
  const startDate = useMemo(
    () => getFilterValue<Date>("startDate") || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    [getFilterValue]
  );
  const endDate = useMemo(() => getFilterValue<Date>("endDate") || new Date(), [getFilterValue]);

  // Warehouse-specific filter getters
  const tagFilter = useMemo(() => getFilterValue<string[]>("tags") || [], [getFilterValue]);
  const opportunityFilter = useMemo(
    () => getFilterValue<string[]>("opportunities") || [],
    [getFilterValue]
  );
  const snowflakeTags = useMemo(
    () => getFilterValue<string[]>("snowflake_tags") || [],
    [getFilterValue]
  );
  const warehouseCostFilter = useMemo(
    () => getFilterValue<string | number>("cost"),
    [getFilterValue]
  );

  const warehouseCostFilterMode = useMemo(() => {
    const value = getFilterValue<string | { value: string; mode: string }>("costFilterMode");
    // If cost is an object with mode, extract the mode
    if (value && typeof value === "object" && "mode" in value) {
      return value.mode;
    }
    // Otherwise get from separate costFilterMode param
    return getFilterValue<string>("costFilterMode") || "lt";
  }, [getFilterValue]);
  const warehouseFilter = useMemo(
    () => getFilterValue<string[]>("warehouse") || [],
    [getFilterValue]
  );
  const autoTuneFilter = useMemo(
    () => getFilterValue<string[]>("autotune") || [],
    [getFilterValue]
  );
  const sizeFilter = useMemo(() => getFilterValue<string[]>("size") || [], [getFilterValue]);
  const instanceFilter = useMemo(
    () => getFilterValue<string[]>("instance") || [],
    [getFilterValue]
  );
  const resizeModeFilter = useMemo(
    () => getFilterValue<string>("resizeMode") || "",
    [getFilterValue]
  );
  const resizeEligibleFilter = useMemo(
    () => getFilterValue<string>("resizeEligible") || "",
    [getFilterValue]
  );
  // Read teams directly from URL params since it's not a visible column
  const teamsFilter = useMemo(() => searchParams.getAll("teams") || [], [searchParams]);

  // Read navigationSource directly from URL params
  const navigationSource = useMemo(
    () => searchParams.get("navigationSource") || undefined,
    [searchParams]
  );

  // Convert filters to API parameters
  const filterParams = useMemo((): GetAllWarehousesParams => {
    const params: GetAllWarehousesParams = {
      page: (page + 1).toString(10),
      size: pageSize.toString(10),
      period,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() || "",
    };

    if (sortAttribute) {
      params.sortAttribute = sortAttribute;
      params.sortOrder = sortOrder;
    }

    if (warehouseFilter.length > 0) params.warehouseName = warehouseFilter;
    if (tagFilter.length > 0) params.tags = tagFilter;
    if (opportunityFilter.length > 0) params.opportunityFilter = opportunityFilter;
    if (snowflakeTags.length > 0) params.snowflakeTags = snowflakeTags;
    if (warehouseCostFilter) {
      params.costFilter = warehouseCostFilter;
      params.costFilterMode = warehouseCostFilterMode as "lt" | "bt" | "gt";
    }
    if (sizeFilter.length > 0) params.warehouseSize = sizeFilter;
    if (autoTuneFilter.length > 0) params.autoTuneFilter = autoTuneFilter;
    if (instanceFilter.length > 0) params.instance_ids = instanceFilter;
    if (resizeEligibleFilter) params.resizeEligible = resizeEligibleFilter;
    if (resizeModeFilter) params.resizeMode = resizeModeFilter;
    if (teamsFilter.length > 0) params.teams = teamsFilter;
    if (navigationSource) params.navigationSource = navigationSource;

    return params;
  }, [
    page,
    pageSize,
    period,
    startDate,
    endDate,
    searchParams,
    warehouseFilter,
    tagFilter,
    opportunityFilter,
    snowflakeTags,
    warehouseCostFilter,
    warehouseCostFilterMode,
    sizeFilter,
    autoTuneFilter,
    instanceFilter,
    resizeEligibleFilter,
    resizeModeFilter,
    teamsFilter,
    navigationSource,
  ]);

  return {
    filterParams,
    isInitialized,
  };
}
